import { useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { useDeleteService, useEvent, useUpdateService } from '@/api/queries';
import { Button } from '@/components/kit/Button';
import { ChoiceGroup, TextLink } from '@/components/kit/Controls';
import { Col, Form, NumberField, Row, TextAreaField, TextField } from '@/components/kit/Field';
import { SelectField } from '@/components/kit/Popover';
import { Breadcrumbs, Columns, Kv, PageHeader } from '@/components/kit/Layout';
import { Alert, EmptyState, ErrorState, Note, TableSkeleton } from '@/components/kit/Note';
import { Panel, Signals, Stack } from '@/components/kit/Panel';
import { Pill } from '@/components/kit/Pill';
import { MobileSub, Table } from '@/components/kit/Table';
import { useToast } from '@/components/kit/Toast';
import { date as fmtDate, money, num, timeRange } from '@/lib/format';
import { SEATING, SELECTION, SERVICE_KIND } from './state';

/**
 * Карточка услуги. Поля зависят от типа: у трёх типов слишком разная
 * природа, чтобы показывать одну форму с половиной пустых полей.
 *
 * Тип после создания не меняется — вместо смены заводят новую услугу.
 */
export function ServicePage() {
  const { eventId, serviceId } = useParams<{ eventId: string; serviceId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const query = useEvent(eventId);
  const update = useUpdateService(eventId!);
  const remove = useDeleteService(eventId!);

  if (query.isError) {
    return (
      <div className="main">
        <Panel>
          <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />
        </Panel>
      </div>
    );
  }

  if (query.isLoading || !query.data) {
    return (
      <div className="main">
        <PageHeader title="Услуга" />
        <Panel flush>
          <TableSkeleton rows={5} cols={3} />
        </Panel>
      </div>
    );
  }

  const event = query.data;
  const service = event.services.find((s) => s.id === serviceId);

  if (!service) {
    return (
      <div className="main">
        <Panel>
          <EmptyState>Услуга не найдена — возможно, её удалили</EmptyState>
        </Panel>
      </div>
    );
  }

  const s = service;
  const kind = SERVICE_KIND[s.kind];
  const block = event.blocks.find((b) => b.id === s.blockId);
  const inPackages = event.packages.filter((p) => p.serviceIds.includes(s.id));

  const fail = (e: Error) => toast.showError(e instanceof ApiError ? e.message : 'Не сохранилось');
  const save = (patch: Record<string, unknown>) =>
    update.mutate({ id: s.id, ...patch }, { onSuccess: () => toast.show('Сохранено'), onError: fail });

  /** Числовые поля — по окончании ввода; промис, чтобы поле откатилось при ошибке */
  const commit = (patch: Record<string, unknown>) =>
    update.mutateAsync({ id: s.id, ...patch }).then(
      () => toast.show('Сохранено'),
      (e: Error) => {
        fail(e);
        throw e;
      },
    );

  const goTab = (tab: string) => navigate(`/events/${event.id}/${tab}`);

  return (
    <div className="main">
      <Breadcrumbs
        items={[
          { label: 'Мероприятия', onClick: () => navigate('/events') },
          { label: event.city, onClick: () => goTab('services') },
          { label: s.name },
        ]}
      />

      <PageHeader title={s.name} before={<Pill tone="mut">{kind.label}</Pill>}>
        <Button
          tone="danger"
          onClick={() =>
            remove.mutate(s.id, {
              onSuccess: () => {
                toast.show(`«${s.name}» удалена`);
                goTab('services');
              },
              onError: fail,
            })
          }
        >
          Удалить
        </Button>
      </PageHeader>

      <Signals>
        {s.kind === 'ticket' && !block ? (
          <Alert>
            <b>Билет без блока.</b>
            <span>Блок удалили — такой билет никуда не пускает и не даст опубликовать мероприятие</span>
          </Alert>
        ) : null}
      </Signals>

      <Columns>
        <Stack>
          <Panel title="Параметры">
            <Form>
              <TextField
                label="Название"
                key={`name-${s.name}`}
                defaultValue={s.name}
                onBlur={(e) => {
                  const next = e.target.value.trim();
                  if (next && next !== s.name) save({ name: next });
                }}
              />

              {s.kind === 'masterclass' ? (
                <>
                  <Row>
                    <Col fix>
                      <TextField
                        label="Начало"
                        placeholder="09:00"
                        size={8}
                        key={`from-${s.startsAt}`}
                        defaultValue={s.startsAt ?? ''}
                        onBlur={(e) => save({ startsAt: e.target.value.trim() || null })}
                      />
                    </Col>
                    <Col fix>
                      <TextField
                        label="Окончание"
                        placeholder="10:20"
                        size={8}
                        key={`to-${s.endsAt}`}
                        defaultValue={s.endsAt ?? ''}
                        onBlur={(e) => save({ endsAt: e.target.value.trim() || null })}
                      />
                    </Col>
                    <Col>
                      <NumberField
                        label="Вместимость"
                        value={s.capacity}
                        onCommit={(v) => commit({ capacity: v })}
                      />
                    </Col>
                  </Row>
                  <Note>
                    Время можно не задавать: расписание классов проясняется позже. Имя хореографа
                    и стиль пишутся прямо в названии — отдельных полей под них нет
                  </Note>
                </>
              ) : null}

              {s.kind === 'ticket' ? (
                <>
                  <Row>
                    <Col>
                      <SelectField
                        label="Блок"
                        value={s.blockId}
                        placeholder="блок удалён"
                        onChange={(v) => save({ blockId: v })}
                        options={event.blocks.map((b) => ({
                          value: b.id,
                          label: b.name,
                          side: fmtDate(b.date),
                        }))}
                      />
                    </Col>
                    <Col>
                      <NumberField
                        label="Вместимость"
                        value={s.capacity}
                        onCommit={(v) => commit({ capacity: v })}
                      />
                    </Col>
                  </Row>
                  <div className="field">
                    <span className="lbl">Рассадка</span>
                    <ChoiceGroup
                      value={s.seating ?? 'free'}
                      onChange={(v) => save({ seating: v })}
                      items={[
                        { value: 'free', label: SEATING.free },
                        { value: 'numbered', label: SEATING.numbered },
                      ]}
                    />
                  </div>
                  <Note>
                    <b>Билет всегда на один блок.</b> Вместимость зала — свойство блока, а не товара:
                    у дневного билета со своей вместимостью получилось бы два счётчика на один зал
                  </Note>
                </>
              ) : null}

              {s.kind === 'showcase' ? (
                <>
                  <TextField
                    label="Хореограф"
                    key={`chor-${s.choreographer}`}
                    defaultValue={s.choreographer ?? ''}
                    onBlur={(e) => save({ choreographer: e.target.value.trim() || null })}
                  />
                  <div className="field">
                    <span className="lbl">Отбор</span>
                    <ChoiceGroup
                      value={s.selection ?? 'none'}
                      onChange={(v) => save({ selection: v })}
                      items={[
                        { value: 'video', label: SELECTION.video },
                        { value: 'none', label: SELECTION.none },
                      ]}
                    />
                  </div>
                </>
              ) : null}

              <TextAreaField
                label="Описание"
                rows={4}
                key={`desc-${s.description}`}
                defaultValue={s.description ?? ''}
                onBlur={(e) => save({ description: e.target.value.trim() || null })}
              />
            </Form>
          </Panel>

          <Panel title="В каких пакетах" flush={inPackages.length > 0}>
            {inPackages.length ? (
              <Table
                head={
                  <tr>
                    <th>Пакет</th>
                    <th>Статус</th>
                    <th className="r">Цена</th>
                  </tr>
                }
              >
                {inPackages.map((p) => (
                  <tr key={p.id} className="clk" onClick={() => navigate(`/events/${event.id}/packages/${p.id}`)}>
                    <td data-col="primary">
                      <span className="nm">{p.name}</span>
                      <MobileSub>{money(p.priceFirst)}</MobileSub>
                    </td>
                    <td data-col="secondary">
                      <Pill tone={p.state === 'live' ? 'ok' : p.state === 'draft' ? 'mut' : 'info'}>
                        {p.state === 'live' ? 'В продаже' : p.state === 'draft' ? 'Черновик' : 'Снят'}
                      </Pill>
                    </td>
                    <td className="r num muted" data-col="sec2">
                      {money(p.priceFirst)}
                    </td>
                  </tr>
                ))}
              </Table>
            ) : (
              <EmptyState>
                Услуга пока не входит ни в один пакет.
                <br />
                <TextLink onClick={() => goTab('packages')}>Открыть пакеты</TextLink>
              </EmptyState>
            )}
          </Panel>
        </Stack>

        <Panel title="Где в программе">
            {s.kind === 'ticket' ? (
              <>
                <Kv k="Блок">
                  {block ? block.name : <span className="wtx">блок удалён</span>}
                </Kv>
                <Kv k="Дата">
                  <span className="num">{block ? fmtDate(block.date) : '—'}</span>
                </Kv>
                <Kv k="Мест">
                  <span className="num">{s.capacity ? num(s.capacity) : '—'}</span>
                </Kv>
              </>
            ) : (
              <>
                <Kv k="Блок">
                  {(() => {
                    const inProgram = event.blocks.find((b) =>
                      b.items.some((i) => i.serviceId === s.id),
                    );
                    return inProgram ? (
                      inProgram.name
                    ) : (
                      <span className="wtx">вне программы</span>
                    );
                  })()}
                </Kv>
                <Kv k="Время">
                  <span className="num">{timeRange(s.startsAt, s.endsAt) ?? '—'}</span>
                </Kv>
              </>
            )}
          <Note>
            Порядок и блок задаются на вкладке «Программа» — там видна вся программа дня.{' '}
            <TextLink onClick={() => goTab('program')}>Открыть программу</TextLink>
          </Note>
        </Panel>
      </Columns>
    </div>
  );
}
